import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../api/client";
import { useI18n } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Plus, X, UserCog, Copy, KeyRound, ShieldCheck, Shield, Power, PowerOff,
  Pencil, Check, Clock,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = ["owner", "admin", "doctor", "nurse", "secretary", "cashier", "stock_manager"];
const SPECIALTIES = ["general", "dentist", "pediatrician", "psychologist", "gynecologist"];
const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

export default function Users() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [banner, setBanner] = useState(null);
  const [dlg, setDlg] = useState(null); // { mode: 'new' | 'edit', user? }

  const isMainAdmin = me.role === "owner" && me.admin_level !== "secondary";

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/users/permissions/catalog")]);
    setItems(u.data); setCatalog(c.data);
  };

  useEffect(() => { load(); }, []);

  const resetPwd = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      setBanner({ email: items.find((u) => u.id === id)?.email, temp_password: data.temp_password });
    } catch (err) { toast.error(apiError(err, t("common.error"))); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (err) { toast.error(apiError(err, t("common.error"))); }
  };

  return (
    <div>
      <PageHeader
        title={t("users.title")}
        actions={
          <button onClick={() => setDlg({ mode: "new" })} data-testid="new-user-btn"
            className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2">
            <Plus className="w-5 h-5" /> {t("users.new")}
          </button>
        }
      />

      {banner && (
        <Card className="mb-6 p-5 bg-[#e3a03b]/10 border-[#e3a03b]/40">
          <div className="flex items-center gap-3">
            <KeyRound className="w-5 h-5 text-[#8a5a10]" />
            <div className="flex-1">
              <div className="font-heading font-bold text-ink">{t("users.tempPassword")} — {banner.email}</div>
              <div className="text-sm text-ink-muted">Communiquez ce mot de passe à l'utilisateur. Il sera demandé de le changer à la première connexion.</div>
              <div className="mt-2 font-mono text-lg text-ink bg-white rounded-lg px-3 py-2 inline-block" data-testid="temp-password">{banner.temp_password}</div>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(banner.temp_password); toast.success("Copié"); }} className="p-2 rounded-lg hover:bg-white">
              <Copy className="w-5 h-5" />
            </button>
            <button onClick={() => setBanner(null)} className="p-2 rounded-lg hover:bg-white"><X className="w-5 h-5" /></button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {items.length === 0 ? <EmptyState icon={UserCog} title={t("common.noData")} /> : (
          <div className="divide-y divide-[var(--border-default)]">
            {items.map((u) => (
              <UserRow key={u.id} u={u} t={t} onEdit={() => setDlg({ mode: "edit", user: u })}
                onReset={() => resetPwd(u.id)} onToggle={() => toggleActive(u)} />
            ))}
          </div>
        )}
      </Card>

      {dlg && catalog && (
        <UserFormDialog
          mode={dlg.mode}
          user={dlg.user}
          catalog={catalog}
          isMainAdmin={isMainAdmin}
          onClose={() => setDlg(null)}
          onDone={({ banner: b }) => { if (b) setBanner(b); setDlg(null); load(); }}
          t={t}
        />
      )}
    </div>
  );
}

function UserRow({ u, t, onEdit, onReset, onToggle }) {
  const isAdmin = u.role === "owner" || u.role === "admin";
  const level = u.admin_level;
  return (
    <div className="p-4 flex items-center gap-4" data-testid={`user-${u.id}`}>
      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold ${isAdmin ? "bg-brand/10 text-brand ring-2 ring-brand/30" : "bg-subtle text-ink"}`}>
        {u.first_name?.[0]}{u.last_name?.[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-ink truncate">{u.first_name} {u.last_name}</div>
          {u.delegation_active && <Badge tone="info"><Clock className="w-3 h-3 inline mr-0.5" /> délégation</Badge>}
        </div>
        <div className="text-sm text-ink-muted truncate">{u.email}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <Badge tone="brand">{t(`roles.${u.role}`)}</Badge>
        {isAdmin && level === "main" && <Badge tone="success"><ShieldCheck className="w-3 h-3 inline mr-0.5" />{t("users.adminMain")}</Badge>}
        {isAdmin && level === "secondary" && <Badge tone="warning"><Shield className="w-3 h-3 inline mr-0.5" />{t("users.adminSecondary")}</Badge>}
        {u.specialty && <Badge tone="info">{t(`specialties.${u.specialty}`)}</Badge>}
        <Badge tone="neutral" className="text-xs">
          {t("users.nPermissions").replace("{n}", (u.effective_permissions || []).length)}
        </Badge>
        {!u.active && <Badge tone="danger">OFF</Badge>}
        {u?.must_change_password && <Badge tone="warning">PWD</Badge>}
      </div>
      <div className="hidden sm:flex gap-1">
        <button onClick={onEdit} data-testid={`u-edit-${u.id}`} title={t("users.edit")}
          className="p-2 rounded-lg hover:bg-brand/10 text-ink hover:text-brand"><Pencil className="w-4 h-4" /></button>
        <button onClick={onReset} data-testid={`u-reset-${u.id}`} title={t("users.resetPassword")}
          className="p-2 rounded-lg hover:bg-brand/10 text-ink hover:text-brand"><KeyRound className="w-4 h-4" /></button>
        <button onClick={onToggle} data-testid={`u-toggle-${u.id}`} title={u.active ? t("users.deactivate") : t("users.activate")}
          className={`p-2 rounded-lg ${u.active ? "hover:bg-terracotta/10 text-ink hover:text-terracotta" : "hover:bg-[#2d8a56]/10 text-ink hover:text-[#2d8a56]"}`}>
          {u.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function UserFormDialog({ mode, user, catalog, isMainAdmin, onClose, onDone, t }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => {
    if (isEdit && user) {
      return {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        specialty: user.specialty || "general",
        phone: user.phone || "",
        initial_password: "",
        permissions: new Set(user.permissions || user.effective_permissions || []),
        admin_level: user.admin_level || null,
        delegation_active: !!user.delegation_active,
        delegation_from: user.delegation_from || "",
        delegation_until: user.delegation_until || "",
        delegated_permissions: new Set(user.delegated_permissions || []),
      };
    }
    return {
      email: "", first_name: "", last_name: "",
      role: "doctor", specialty: "general", phone: "", initial_password: "",
      permissions: new Set(catalog.defaults.doctor || []),
      admin_level: null,
      delegation_active: false, delegation_from: "", delegation_until: "",
      delegated_permissions: new Set(),
    };
  });

  // Reset permissions to role defaults when role changes (new user only)
  const changeRole = (role) => {
    const next = { ...form, role };
    if (!isEdit) {
      next.permissions = new Set(catalog.defaults[role] || []);
    }
    if (role === "owner") next.admin_level = "main";
    else if (role === "admin") next.admin_level = "secondary";
    else next.admin_level = null;
    setForm(next);
  };

  const togglePerm = (perm, set = "permissions") => {
    const s = new Set(form[set]);
    if (s.has(perm)) s.delete(perm); else s.add(perm);
    setForm({ ...form, [set]: s });
  };

  const setAllPerms = (value) => {
    setForm({ ...form, permissions: new Set(value ? catalog.all : []) });
  };

  const resetToRole = () => {
    setForm({ ...form, permissions: new Set(catalog.defaults[form.role] || []) });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        phone: form.phone,
        permissions: Array.from(form.permissions),
        admin_level: form.admin_level,
      };
      if (["doctor", "nurse"].includes(form.role)) payload.specialty = form.specialty;
      if (form.delegation_active) {
        payload.delegation_active = true;
        payload.delegation_from = form.delegation_from || null;
        payload.delegation_until = form.delegation_until || null;
        payload.delegated_permissions = Array.from(form.delegated_permissions);
      } else {
        payload.delegation_active = false;
      }

      if (isEdit) {
        // Omit email (immutable here)
        delete payload.email;
        await api.patch(`/users/${user.id}`, payload);
        toast.success(t("common.success"));
        onDone({});
      } else {
        if (form.initial_password) payload.initial_password = form.initial_password;
        const { data } = await api.post("/users", payload);
        onDone({
          banner: { email: data.email, temp_password: data.initial_password },
        });
      }
    } catch (err) { toast.error(apiError(err, t("common.error"))); }
  };

  const isAdminRole = form.role === "owner" || form.role === "admin";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="user-form-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-xl">{isEdit ? t("users.edit") : t("users.new")}</h3>
          <button onClick={onClose} data-testid="u-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block mb-2 text-sm font-semibold">{t("patients.firstName")}</label>
              <input required className={INPUT} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="u-first" /></div>
            <div><label className="block mb-2 text-sm font-semibold">{t("patients.lastName")}</label>
              <input required className={INPUT} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="u-last" /></div>
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold">{t("auth.email")}</label>
            <input type="email" required disabled={isEdit} className={`${INPUT} ${isEdit ? "bg-subtle" : ""}`}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-2 text-sm font-semibold">{t("users.role")}</label>
              <select className={INPUT} value={form.role} onChange={(e) => changeRole(e.target.value)} data-testid="u-role">
                {ROLES.map((r) => (
                  <option key={r} value={r} disabled={!isMainAdmin && (r === "owner" || r === "admin")}>
                    {t(`roles.${r}`)}{!isMainAdmin && (r === "owner" || r === "admin") ? " (main admin only)" : ""}
                  </option>
                ))}
              </select>
            </div>
            {["doctor", "nurse"].includes(form.role) && (
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("users.specialty")}</label>
                <select className={INPUT} value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} data-testid="u-specialty">
                  {SPECIALTIES.map((s) => <option key={s} value={s}>{t(`specialties.${s}`)}</option>)}
                </select>
              </div>
            )}
            {isAdminRole && isMainAdmin && (
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("users.adminLevel")}</label>
                <select className={INPUT} value={form.admin_level || ""} onChange={(e) => setForm({ ...form, admin_level: e.target.value })} data-testid="u-admin-level">
                  <option value="main">{t("users.main")}</option>
                  <option value="secondary">{t("users.secondary")}</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block mb-2 text-sm font-semibold">{t("common.phone")}</label>
            <input className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          {!isEdit && (
            <div>
              <label className="block mb-2 text-sm font-semibold">{t("users.initialPassword")}</label>
              <input className={INPUT} value={form.initial_password} onChange={(e) => setForm({ ...form, initial_password: e.target.value })} placeholder="(auto)" />
            </div>
          )}

          {/* PERMISSIONS MATRIX */}
          <div className="rounded-2xl border border-[var(--border-default)] p-4" data-testid="u-perms">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-heading font-bold text-ink">{t("users.permissions")}</div>
                <div className="text-xs text-ink-muted">{t("users.permissionsHint")}</div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <button type="button" onClick={() => setAllPerms(true)} data-testid="perm-select-all" className="px-3 py-1.5 rounded-lg bg-subtle hover:bg-brand/10 hover:text-brand font-semibold">{t("users.selectAll")}</button>
                <button type="button" onClick={() => setAllPerms(false)} data-testid="perm-deselect-all" className="px-3 py-1.5 rounded-lg bg-subtle hover:bg-terracotta/10 hover:text-terracotta font-semibold">{t("users.deselectAll")}</button>
                <button type="button" onClick={resetToRole} data-testid="perm-reset-role" className="px-3 py-1.5 rounded-lg bg-subtle hover:bg-brand/10 hover:text-brand font-semibold">{t("users.resetToRole")}</button>
              </div>
            </div>

            <PermMatrix catalog={catalog} selected={form.permissions} onToggle={(p) => togglePerm(p)} t={t} />

            {/* Meta perms */}
            <div className="mt-3 pt-3 border-t border-[var(--border-default)] grid grid-cols-1 sm:grid-cols-2 gap-2">
              {catalog.meta_permissions.map((p) => {
                const [, action] = p.split(":");
                return (
                  <label key={p} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-subtle cursor-pointer" data-testid={`perm-meta-${action}`}>
                    <input type="checkbox" checked={form.permissions.has(p)} onChange={() => togglePerm(p)} />
                    <span className="font-medium">{t(`users.actions.${action}`) || action}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Delegation (edit mode only) */}
          {isEdit && (
            <div className="rounded-2xl border border-[var(--border-default)] p-4" data-testid="u-delegation">
              <label className="flex items-center gap-2 font-heading font-bold text-ink cursor-pointer">
                <input type="checkbox" checked={form.delegation_active}
                  onChange={(e) => setForm({ ...form, delegation_active: e.target.checked })} data-testid="u-delegation-active" />
                <Clock className="w-4 h-4 text-brand" /> {t("users.delegation")}
              </label>
              <div className="text-xs text-ink-muted mt-1 mb-3">{t("users.delegationHint")}</div>
              {form.delegation_active && (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block mb-1 text-xs font-semibold">{t("users.delegationFrom")}</label>
                      <input type="date" className={INPUT} value={form.delegation_from} onChange={(e) => setForm({ ...form, delegation_from: e.target.value })} />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold">{t("users.delegationUntil")}</label>
                      <input type="date" className={INPUT} value={form.delegation_until} onChange={(e) => setForm({ ...form, delegation_until: e.target.value })} />
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-ink mb-2">{t("users.delegatedPerms")}</div>
                  <PermMatrix catalog={catalog} selected={form.delegated_permissions}
                    onToggle={(p) => togglePerm(p, "delegated_permissions")} t={t} compact />
                </>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-1">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">{t("common.cancel")}</button>
            <button type="submit" data-testid="u-save" className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> {t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PermMatrix({ catalog, selected, onToggle, t, compact = false }) {
  const cellCls = "text-center py-1.5 px-1";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-ink-muted">
            <th className="text-left py-1 px-2">Module</th>
            {catalog.actions.map((a) => (
              <th key={a} className="text-center py-1 px-1">{t(`users.actions.${a}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {catalog.modules.map((m) => (
            <tr key={m} className="border-t border-[var(--border-default)]">
              <td className="py-1.5 px-2 font-semibold text-ink">{t(`users.modules.${m}`)}</td>
              {catalog.actions.map((a) => {
                const perm = `${m}:${a}`;
                const valid = catalog.all.includes(perm);
                if (!valid) return <td key={a} className={cellCls}>—</td>;
                return (
                  <td key={a} className={cellCls}>
                    <input type="checkbox" checked={selected.has(perm)} onChange={() => onToggle(perm)}
                      data-testid={`perm-${m}-${a}`} className={`w-4 h-4 accent-[var(--brand)] ${compact ? "opacity-90" : ""}`} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
